/**
 * Collection Frame Upload — Upload UI + Toolbar
 * Handles file input, Firebase upload, color filter, sort, and UI state.
 * Delegates card rendering to the global frame-card-renderer.js.
 */
(function () {
  'use strict';

  var MAX_SIZE = 25 * 1024 * 1024;
  var ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/webp'];

  var state = { blobUrl: null, firebaseUrl: null, fileName: null };

  /* --- DOM refs --- */
  var fileInput = document.getElementById('cfuFileInput');
  var uploadBtn = document.getElementById('cfuUploadBtn');
  var promptEl = document.getElementById('cfuPrompt');
  var previewEl = document.getElementById('cfuPreview');
  var thumbEl = document.getElementById('cfuThumb');
  var removeBtn = document.getElementById('cfuRemove');
  var filenameEl = document.getElementById('cfuFilename');
  var sortSelect = document.getElementById('cfuSortBy');
  var colorContainer = document.getElementById('cfuColorFilters');

  if (!fileInput) return;

  /* --- IndexedDB helpers --- */
  function saveImageToIDB(blob, firebaseUrl) {
    try {
      var req = indexedDB.open('fc-images', 1);
      req.onupgradeneeded = function (e) { e.target.result.createObjectStore('blobs'); };
      req.onsuccess = function (e) {
        var db = e.target.result;
        var tx = db.transaction('blobs', 'readwrite');
        var store = tx.objectStore('blobs');
        if (blob) store.put(blob, 'uploadedImage');
        if (firebaseUrl) store.put(firebaseUrl, 'firebaseUrl');
      };
    } catch (e) { /* ignore */ }
  }

  function clearImageFromIDB() {
    try {
      var req = indexedDB.open('fc-images', 1);
      req.onsuccess = function (e) {
        var db = e.target.result;
        var tx = db.transaction('blobs', 'readwrite');
        var store = tx.objectStore('blobs');
        store.delete('uploadedImage');
        store.delete('firebaseUrl');
      };
    } catch (e) { /* ignore */ }
  }

  function loadImageFromIDB(callback) {
    try {
      var req = indexedDB.open('fc-images', 1);
      req.onupgradeneeded = function (e) { e.target.result.createObjectStore('blobs'); };
      req.onsuccess = function (e) {
        var db = e.target.result;
        var tx = db.transaction('blobs', 'readonly');
        var store = tx.objectStore('blobs');
        var getBlob = store.get('uploadedImage');
        var getUrl = store.get('firebaseUrl');
        var result = { blob: null, firebaseUrl: null };
        getBlob.onsuccess = function () { result.blob = getBlob.result || null; };
        getUrl.onsuccess = function () { result.firebaseUrl = getUrl.result || null; };
        tx.oncomplete = function () { callback(result); };
        tx.onerror = function () { callback(result); };
      };
      req.onerror = function () { callback({ blob: null, firebaseUrl: null }); };
    } catch (e) { callback({ blob: null, firebaseUrl: null }); }
  }

  /* --- Firebase upload --- */
  var firebaseInitPromise = null;
  var cachedFirebaseApp = null;
  var cachedAppCheck = null;
  var cachedBucket = null;

  function initFirebase() {
    if (firebaseInitPromise) return firebaseInitPromise;
    firebaseInitPromise = Promise.resolve()
      .then(function () {
        var configEl = document.getElementById('cfu-firebase-config');
        if (!configEl) throw new Error('Firebase config not found');
        var config = JSON.parse(configEl.textContent);
        if (!config.firebase || !config.firebase.apiKey) throw new Error('Invalid Firebase config');
        cachedBucket = config.firebase.storageBucket;
        var recaptchaSiteKey = config.recaptcha && config.recaptcha.siteKey;
        if (!firebase.apps.length) {
          cachedFirebaseApp = firebase.initializeApp(config.firebase);
        } else {
          cachedFirebaseApp = firebase.apps[0];
        }
        if (recaptchaSiteKey && !cachedAppCheck) {
          cachedAppCheck = firebase.appCheck(cachedFirebaseApp);
          cachedAppCheck.activate(new firebase.appCheck.ReCaptchaV3Provider(recaptchaSiteKey), true);
        }
        return firebase.auth(cachedFirebaseApp).signInAnonymously();
      })
      .then(function (uc) {
        var authP = uc.user.getIdToken(true);
        var appCheckP = cachedAppCheck ? cachedAppCheck.getToken(false).then(function (r) { return r.token; }) : Promise.resolve(null);
        return Promise.all([authP, appCheckP]);
      })
      .then(function (t) { return { authToken: t[0], appCheckToken: t[1], bucket: cachedBucket }; })
      .catch(function (err) { firebaseInitPromise = null; throw err; });
    return firebaseInitPromise;
  }

  function getFreshTokens() {
    var user = cachedFirebaseApp && firebase.auth(cachedFirebaseApp).currentUser;
    if (user) {
      var authP = user.getIdToken(false);
      var appCheckP = cachedAppCheck ? cachedAppCheck.getToken(false).then(function (r) { return r.token; }) : Promise.resolve(null);
      return Promise.all([authP, appCheckP]).then(function (t) { return { authToken: t[0], appCheckToken: t[1], bucket: cachedBucket }; });
    }
    firebaseInitPromise = null;
    return initFirebase();
  }

  function uploadToFirebase(file) {
    var timestamp = new Date().toISOString().replace(/[:.]/g, '-');
    var randomId = Math.floor(Math.random() * 10000).toString().padStart(4, '0');
    var ext = file.type === 'image/png' ? 'png' : file.type === 'image/webp' ? 'webp' : 'jpg';
    var storagePath = 'uploads/processed-' + timestamp + '-' + randomId + '.' + ext;

    initFirebase()
      .then(function () { return getFreshTokens(); })
      .then(function (result) {
        var uploadUrl = 'https://firebasestorage.googleapis.com/v0/b/' +
          encodeURIComponent(result.bucket) + '/o?uploadType=media&name=' + encodeURIComponent(storagePath);
        return new Promise(function (resolve, reject) {
          var xhr = new XMLHttpRequest();
          xhr.open('POST', uploadUrl);
          xhr.setRequestHeader('Authorization', 'Bearer ' + result.authToken);
          xhr.setRequestHeader('Content-Type', file.type);
          if (result.appCheckToken) xhr.setRequestHeader('X-Firebase-AppCheck', result.appCheckToken);
          xhr.onload = function () {
            if (xhr.status === 200) {
              try {
                var data = JSON.parse(xhr.responseText);
                resolve('https://firebasestorage.googleapis.com/v0/b/' + encodeURIComponent(result.bucket) +
                  '/o/' + encodeURIComponent(data.name) + '?alt=media&token=' + (data.downloadTokens || ''));
              } catch (e) { reject(new Error('Parse error')); }
            } else { reject(new Error('Upload failed (' + xhr.status + ')')); }
          };
          xhr.onerror = function () { reject(new Error('Network error')); };
          xhr.send(file);
        });
      })
      .then(function (downloadUrl) {
        state.firebaseUrl = downloadUrl;
        saveImageToIDB(null, downloadUrl);
        try { sessionStorage.setItem('fc-transfer-imageUrl', downloadUrl); } catch (e) { /* ignore */ }
        if (window.frameCardRenderer) {
          var s = window.frameCardRenderer.getState();
          window.frameCardRenderer.setImage(s.blobUrl, downloadUrl, s.imageAspectRatio);
        }
      })
      .catch(function (err) {
        console.error('[CollectionUpload] Firebase error:', err.message || err);
      });
  }

  /* --- File handling --- */
  function handleFile(file) {
    if (!file) return;
    if (ALLOWED_TYPES.indexOf(file.type) === -1) { alert('Nur JPG, PNG oder WebP Dateien erlaubt.'); return; }
    if (file.size > MAX_SIZE) { alert('Datei zu gro\u00df (max. 25 MB).'); return; }

    if (state.blobUrl) URL.revokeObjectURL(state.blobUrl);
    state.blobUrl = URL.createObjectURL(file);
    state.fileName = file.name;

    saveImageToIDB(file);
    uploadToFirebase(file);

    var img = new Image();
    img.onload = function () {
      var ar = img.naturalWidth / img.naturalHeight;
      showPreviewUI();
      if (window.frameCardRenderer) {
        window.frameCardRenderer.setImage(state.blobUrl, state.firebaseUrl, ar);
      }
    };
    img.src = state.blobUrl;
  }

  /* --- UI state --- */
  function showPreviewUI() {
    if (promptEl) promptEl.style.display = 'none';
    if (previewEl) previewEl.style.display = '';
    if (thumbEl) thumbEl.src = state.blobUrl;
    if (filenameEl) filenameEl.textContent = state.fileName;
    // Swap upload-button label → "Anderes Bild"
    if (uploadBtn) {
      var lbl = uploadBtn.querySelector('.collection-filter-bar__btn-label');
      if (lbl) lbl.textContent = 'Anderes Bild';
      uploadBtn.classList.add('collection-filter-bar__btn--has-image');
    }
  }

  function showPromptUI() {
    if (previewEl) previewEl.style.display = 'none';
    if (promptEl) promptEl.style.display = '';
    if (thumbEl) thumbEl.src = '';
    if (filenameEl) filenameEl.textContent = '';
    if (uploadBtn) {
      var lbl = uploadBtn.querySelector('.collection-filter-bar__btn-label');
      if (lbl) lbl.textContent = 'Bild hochladen';
      uploadBtn.classList.remove('collection-filter-bar__btn--has-image');
    }
  }

  function removeImage() {
    if (state.blobUrl) URL.revokeObjectURL(state.blobUrl);
    state.blobUrl = null;
    state.firebaseUrl = null;
    state.fileName = null;
    fileInput.value = '';
    clearImageFromIDB();
    try { sessionStorage.removeItem('fc-transfer-imageUrl'); } catch (e) { /* ignore */ }
    showPromptUI();
    if (window.frameCardRenderer) window.frameCardRenderer.clearImage();
  }

  /* --- Event listeners --- */
  fileInput.addEventListener('change', function () {
    var file = fileInput.files && fileInput.files[0];
    if (file) handleFile(file);
  });
  removeBtn.addEventListener('click', removeImage);

  if (uploadBtn) {
    uploadBtn.addEventListener('click', function (e) {
      e.preventDefault();
      fileInput.click();
    });
  }

  /* --- Sort --- */
  if (sortSelect) {
    sortSelect.addEventListener('change', function () {
      var url = new URL(window.location.href);
      url.searchParams.set('sort_by', sortSelect.value);
      window.location.href = url.toString();
    });
  }

  /* --- Color filter (tag-based, like configurator) --- */
  function initColorFilter() {
    if (!colorContainer) return;

    var cards = document.querySelectorAll('#product-grid .card-wrapper[data-frame-product]');
    var colorSet = {};
    for (var i = 0; i < cards.length; i++) {
      var colorsAttr = cards[i].getAttribute('data-frame-colors');
      if (!colorsAttr) continue;
      var parts = colorsAttr.split(',');
      for (var p = 0; p < parts.length; p++) {
        var c = parts[p].trim();
        if (c) colorSet[c] = true;
      }
    }

    var uniqueColors = Object.keys(colorSet).sort();
    if (uniqueColors.length < 2) return;

    // "Alle" tab
    var allBtn = document.createElement('button');
    allBtn.type = 'button';
    allBtn.className = 'collection-toolbar__color-tab collection-toolbar__color-tab--active';
    allBtn.textContent = 'Alle';
    allBtn.setAttribute('data-filter', 'Alle');
    colorContainer.appendChild(allBtn);

    // Color tabs
    for (var j = 0; j < uniqueColors.length; j++) {
      var btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'collection-toolbar__color-tab';
      btn.textContent = uniqueColors[j];
      btn.setAttribute('data-filter', uniqueColors[j]);
      colorContainer.appendChild(btn);
    }

    // Click handler
    colorContainer.addEventListener('click', function (e) {
      var tab = e.target.closest('.collection-toolbar__color-tab');
      if (!tab) return;

      var tabs = colorContainer.querySelectorAll('.collection-toolbar__color-tab');
      for (var k = 0; k < tabs.length; k++) {
        tabs[k].classList.remove('collection-toolbar__color-tab--active');
      }
      tab.classList.add('collection-toolbar__color-tab--active');

      var selectedColor = tab.getAttribute('data-filter');
      filterByColor(selectedColor === 'Alle' ? '' : selectedColor);
    });
  }

  function filterByColor(color) {
    var allItems = document.querySelectorAll('#product-grid .grid__item');
    for (var i = 0; i < allItems.length; i++) {
      var item = allItems[i];
      if (!color) {
        item.classList.remove('grid__item--color-hidden');
      } else {
        var card = item.querySelector('.card-wrapper[data-frame-colors]');
        var colorsAttr = card ? card.getAttribute('data-frame-colors') : '';
        var colors = colorsAttr ? colorsAttr.split(',').map(function (c) { return c.trim(); }) : [];
        if (colors.indexOf(color) !== -1) {
          item.classList.remove('grid__item--color-hidden');
        } else {
          item.classList.add('grid__item--color-hidden');
        }
      }
    }
  }

  /* --- Restore upload UI from IDB (for back navigation) --- */
  loadImageFromIDB(function (data) {
    if (!data.blob && !data.firebaseUrl) return;
    if (data.blob) {
      state.blobUrl = URL.createObjectURL(data.blob);
      if (data.firebaseUrl) state.firebaseUrl = data.firebaseUrl;
      state.fileName = 'Gespeichertes Foto';
      showPreviewUI();
    } else if (data.firebaseUrl) {
      state.firebaseUrl = data.firebaseUrl;
      state.blobUrl = data.firebaseUrl;
      state.fileName = 'Gespeichertes Foto';
      showPreviewUI();
    }
  });

  /* --- Tag filter (Schattenfugenrahmen) --- */
  function initTagFilter() {
    var tagContainer = document.getElementById('cfuTagFilters');
    if (!tagContainer) return;

    tagContainer.addEventListener('click', function (e) {
      var tab = e.target.closest('[data-tag-filter]');
      if (!tab) return;

      var tabs = tagContainer.querySelectorAll('[data-tag-filter]');
      for (var k = 0; k < tabs.length; k++) {
        tabs[k].classList.remove('collection-toolbar__color-tab--active');
      }
      tab.classList.add('collection-toolbar__color-tab--active');

      var selectedTag = tab.getAttribute('data-tag-filter');
      filterByTag(selectedTag === 'all' ? '' : selectedTag);
    });
  }

  function filterByTag(tag) {
    var allItems = document.querySelectorAll('#product-grid .grid__item');
    for (var i = 0; i < allItems.length; i++) {
      var item = allItems[i];
      if (!tag) {
        item.classList.remove('grid__item--tag-hidden');
      } else {
        var card = item.querySelector('.card-wrapper[data-frame-product]');
        if (!card) {
          item.classList.add('grid__item--tag-hidden');
          continue;
        }
        if (tag === 'schatten' && card.hasAttribute('data-frame-schatten')) {
          item.classList.remove('grid__item--tag-hidden');
        } else {
          item.classList.add('grid__item--tag-hidden');
        }
      }
    }
  }

  /* --- View Toggle (1 col / 2 col on mobile) --- */
  function initViewToggle() {
    var toggle = document.getElementById('cfuViewToggle');
    if (!toggle) return;

    var STORAGE_KEY = 'framelin:collection-view';
    var buttons = toggle.querySelectorAll('.collection-view-toggle__btn');

    function applyView(view) {
      var grids = document.querySelectorAll('#product-grid');
      grids.forEach(function (grid) {
        grid.classList.toggle('product-grid--mobile-view-1', view === '1');
        grid.classList.toggle('product-grid--mobile-view-2', view === '2');
      });
      buttons.forEach(function (btn) {
        var active = btn.getAttribute('data-view') === view;
        btn.classList.toggle('is-active', active);
        btn.setAttribute('aria-pressed', active ? 'true' : 'false');
      });
    }

    var saved = null;
    try { saved = localStorage.getItem(STORAGE_KEY); } catch (e) {}
    applyView(saved === '2' ? '2' : '1');

    buttons.forEach(function (btn) {
      btn.addEventListener('click', function () {
        var view = btn.getAttribute('data-view');
        try { localStorage.setItem(STORAGE_KEY, view); } catch (e) {}
        applyView(view);
      });
    });

    // Re-apply view after AJAX filter updates re-render the grid
    var observerTarget = document.getElementById('ProductGridContainer');
    if (observerTarget && window.MutationObserver) {
      var observer = new MutationObserver(function () {
        var current = null;
        try { current = localStorage.getItem(STORAGE_KEY); } catch (e) {}
        applyView(current === '2' ? '2' : '1');
      });
      observer.observe(observerTarget, { childList: true, subtree: true });
    }
  }

  /* --- Init filters --- */
  initColorFilter();
  initTagFilter();
  initViewToggle();
})();
